
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useLoading } from "@/contexts/LoadingContext";

export const RouteChangeHandler = () => {
  const location = useLocation();
  const { setPageLoading } = useLoading();
  const previousLocation = useRef(location.pathname);
  const lastChangeTime = useRef(Date.now());
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fallbackTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clear loading state on mount to handle edge cases
  useEffect(() => {
    console.log("RouteChangeHandler: Component mounted, clearing any stuck loading state");
    setPageLoading(false);
  }, [setPageLoading]);

  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousLocation.current;
    const now = Date.now();
    
    // Clear any existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current);
      fallbackTimeoutRef.current = null;
    }
    
    // Prevent rapid route changes that could cause loops
    if (now - lastChangeTime.current < 50) {
      console.log(`RouteChangeHandler: Ignoring rapid route change (${now - lastChangeTime.current}ms)`);
      previousLocation.current = currentPath; // Always update reference
      return;
    }
    
    // Only handle actual route changes
    if (previousPath !== currentPath) {
      console.log(`RouteChangeHandler: Route change ${previousPath} -> ${currentPath}`);
      lastChangeTime.current = now;
      
      // Detect if this is browser navigation (back/forward)
      const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      const isBrowserNavigation = navigationEntries[0]?.type === 'back_forward' ||
                                  window.history.state?.__navigationId !== undefined;
      
      console.log(`RouteChangeHandler: Navigation type - ${isBrowserNavigation ? 'Browser' : 'Programmatic'}`);
      
      // Configuration pages don't need loading states
      const configPages = ['/ai-context', '/settings'];
      const isConfigPage = configPages.includes(currentPath);
      
      // Skip loading when on root (which redirects immediately) or config pages
      const isOnRoot = currentPath === '/';
      
      if (isConfigPage) {
        console.log(`RouteChangeHandler: Config page ${currentPath}, skipping loading`);
        setPageLoading(false);
      } else if (isOnRoot) {
        console.log(`RouteChangeHandler: Root page ${currentPath}, skipping loading (will redirect)`);
        setPageLoading(false);
      } else {
        // Show brief loading for workspace pages during route transition
        console.log(`RouteChangeHandler: Workspace page ${currentPath}, showing brief route loading (${isBrowserNavigation ? 'browser nav' : 'programmatic'})`);
        setPageLoading(true, "Loading page...");
        
        // Use longer timeout for browser navigation, shorter for programmatic
        const timeoutDuration = isBrowserNavigation ? 300 : 200;
        
        // Clear loading after transition
        timeoutRef.current = setTimeout(() => {
          console.log(`RouteChangeHandler: Clearing route loading for ${currentPath} after ${timeoutDuration}ms`);
          setPageLoading(false);
          timeoutRef.current = null;
        }, timeoutDuration);
        
        // Fallback timeout to ensure loading never gets stuck (safety net)
        fallbackTimeoutRef.current = setTimeout(() => {
          console.warn(`RouteChangeHandler: Fallback clearing loading for ${currentPath} after 2s (safety net)`);
          setPageLoading(false);
          fallbackTimeoutRef.current = null;
        }, 2000);
      }
      
      // Always update the previous location reference
      previousLocation.current = currentPath;
    }

    // Cleanup function to clear timeouts if component unmounts or location changes again
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = null;
      }
    };
  }, [location.pathname, setPageLoading]);

  return null;
};
